import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://codelens.epoch.co/openshield/';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 7200000,  // 2 hours for auto-fix operations
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Add request interceptor for Cognito token authentication
apiClient.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('cognito_token');
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('cognito_token');
      localStorage.removeItem('cognito_id_token');
      window.location.href = 'https://codelens.epoch.co/';
    }
    return Promise.reject(error);
  }
);

class VulnerabilityApiService {
  // Get all vulnerabilities
  async getAllVulnerabilities() {
    try {
      const response = await apiClient.get('/vulnerabilities/');
      return response.data;
    } catch (error) {
      console.error('Error fetching vulnerabilities:', error);
      throw error;
    }
  }

  // Get vulnerabilities for a specific agent
  async getVulnerabilitiesByAgent(agentId) {
    try {
      const response = await apiClient.get(`/vulnerabilities/${agentId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching vulnerabilities for agent ${agentId}:`, error);
      throw error;
    }
  }

  // Get vulnerability statistics
  async getVulnerabilityStats() {
    try {
      const response = await apiClient.get('/vulnerabilities/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching vulnerability statistics:', error);
      throw error;
    }
  }

  // Download CVE report for a specific agent
  async downloadCVEReport(agentId) {
    try {
      const response = await apiClient.get(`/vulnerabilities/${agentId}/download/cve`);
      return response.data;
    } catch (error) {
      console.error(`Error downloading CVE report for agent ${agentId}:`, error);
      throw error;
    }
  }

  // Download Package report for a specific agent
  async downloadPackageReport(agentId) {
    try {
      const response = await apiClient.get(`/vulnerabilities/${agentId}/download/packages`);
      return response.data;
    } catch (error) {
      console.error(`Error downloading package report for agent ${agentId}:`, error);
      throw error;
    }
  }

  // Process vulnerability data for display
  processVulnerabilityData(vulnerabilities) {
    if (!vulnerabilities || vulnerabilities.length === 0) {
      return {
        totalVulnerabilities: 0,
        severityStats: { critical: 0, high: 0, medium: 0, low: 0, info: 0 },
        recentVulnerabilities: [],
        topPackages: [],
        criticalVulnerabilities: []
      };
    }

    const severityStats = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
    const packageCounts = {};
    const criticalVulns = [];

    vulnerabilities.forEach(vuln => {
      const severity = vuln.vulnerability?.severity?.toLowerCase() || 'info';
      if (severityStats.hasOwnProperty(severity)) {
        severityStats[severity]++;
      } else {
        severityStats.info++;
      }

      // Track packages
      const packageName = vuln.package?.name;
      if (packageName) {
        packageCounts[packageName] = (packageCounts[packageName] || 0) + 1;
      }

      // Collect critical and high severity vulnerabilities
      if (severity === 'critical' || severity === 'high') {
        criticalVulns.push(vuln);
      }
    });

    // Sort vulnerabilities by detection date (most recent first)
    const recentVulnerabilities = [...vulnerabilities]
      .sort((a, b) => new Date(b.vulnerability?.detected_at || 0) - new Date(a.vulnerability?.detected_at || 0))
      .slice(0, 10);

    // Get top affected packages
    const topPackages = Object.entries(packageCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([name, count]) => ({ name, count }));

    // Sort critical vulnerabilities by CVSS score (highest first)
    const sortedCriticalVulns = criticalVulns
      .sort((a, b) => (b.vulnerability?.score?.base || 0) - (a.vulnerability?.score?.base || 0))
      .slice(0, 20);

    return {
      totalVulnerabilities: vulnerabilities.length,
      severityStats,
      recentVulnerabilities,
      topPackages,
      criticalVulnerabilities: sortedCriticalVulns
    };
  }

  // Get severity color for UI display
  getSeverityColor(severity) {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  }

  // Format CVSS score for display
  formatCvssScore(score) {
    if (!score || !score.base) return 'N/A';
    return `${score.base.toFixed(1)} (${score.version || 'N/A'})`;
  }

  // Get severity badge color
  getSeverityBadgeColor(severity) {
    switch (severity?.toLowerCase()) {
      case 'critical': return '#d32f2f';
      case 'high': return '#f57c00';
      case 'medium': return '#fbc02d';
      case 'low': return '#388e3c';
      default: return '#757575';
    }
  }
}

// Create and export a singleton instance
const vulnerabilityApiService = new VulnerabilityApiService();
export default vulnerabilityApiService;
